/**
 * LibraryGenerator — admin-only bulk asset generation from a CSV file.
 *
 * CSV schema (new): type, pack, category, folder_path, name, description,
 *                   background, aspect_ratio, style_mode, priority, status
 * CSV schema (legacy): category, name, description
 * JSON schema: [{kind, name, custom_prompt, ...}]
 */
import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { Upload, Play, Zap, Loader2, CheckCircle2, XCircle, Clock, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { startLibraryBatch, cancelLibraryBatch, getLibraryBatchJob, getLibraryBatchJobs } from '../lib/api';

// ── Client-side helpers ──────────────────────────────────────────────────────

// Maps `type` column → internal kind
const TYPE_MAP = {
  character: 'characters', characters: 'characters',
  personnage: 'characters', personnages: 'characters',
  decor: 'decors', décor: 'decors', décors: 'decors', decors: 'decors',
  prop: 'objects', props: 'objects',
  object: 'objects', objects: 'objects', objet: 'objects', objets: 'objects',
};

const KIND_LABELS = { characters: 'Personnage', decors: 'Décor', objects: 'Objet' };
const KIND_COLORS = {
  characters: 'bg-blue-500/10 text-blue-600',
  decors: 'bg-green-500/10 text-green-600',
  objects: 'bg-orange-500/10 text-orange-600',
};

const BG_LABELS = { white: 'blanc', transparent: 'transparent', none: 'aucun' };

/** Try to fix latin-1 misread as UTF-8 (Ã© → é) */
function fixEncoding(text) {
  if (!/Ã[-¿]/.test(text)) return text;
  try {
    const bytes = new Uint8Array([...text].map(c => c.charCodeAt(0) & 0xff));
    return new TextDecoder('iso-8859-1').decode(bytes);
  } catch { return text; }
}

/** Read CSV cols handling quoted values */
function splitCSVLine(line) {
  const cols = [];
  let cur = '', inQ = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') { inQ = !inQ; }
    else if (ch === ',' && !inQ) { cols.push(cur.trim()); cur = ''; }
    else { cur += ch; }
  }
  cols.push(cur.trim());
  return cols;
}

/** Parse CSV — supports both new extended schema and legacy (category, name, description) */
function parseCSV(text) {
  text = fixEncoding(text);
  const lines = text.trim().split(/\r?\n/).filter(l => l.trim());
  if (lines.length < 2) throw new Error('Le fichier CSV doit contenir au moins une ligne de données.');
  const headers = splitCSVLine(lines[0]).map(h => h.replace(/^["']|["']$/g, '').toLowerCase().trim());
  const idx = k => headers.indexOf(k);

  const isNewSchema = headers.includes('type');

  return lines.slice(1).filter(l => l.trim()).map((line, i) => {
    const cols = splitCSVLine(line);
    const get = k => (cols[idx(k)] || '').replace(/^"|"$/g, '').trim();

    if (isNewSchema) {
      return {
        row: i + 2,
        raw_type: get('type'),
        category: get('category'),
        folder_path: get('folder_path'),
        name: get('name'),
        description: get('description'),
        background: get('background') || 'white',
        aspect_ratio: get('aspect_ratio') || '1:1',
        priority: parseInt(get('priority') || '1', 10) || 1,
      };
    } else {
      // Legacy: category = kind
      return {
        row: i + 2,
        raw_type: get('category'),
        category: '',
        folder_path: '',
        name: get('name'),
        description: get('description'),
        background: 'white',
        aspect_ratio: '1:1',
        priority: 1,
      };
    }
  });
}

/** Parse JSON → rows */
function parseJSON(text) {
  const data = JSON.parse(text);
  if (!Array.isArray(data)) throw new Error("Le JSON doit être un tableau d'objets.");
  return data.map((item, i) => ({
    row: i + 1,
    raw_type: (item.type || item.kind || item.category || '').trim(),
    category: (item.category || '').trim(),
    folder_path: (item.folder_path || '').trim(),
    name: (item.name || '').trim(),
    description: (item.description || item.custom_prompt || '').trim(),
    background: item.background || 'white',
    aspect_ratio: item.aspect_ratio || '1:1',
    priority: parseInt(item.priority || 1, 10) || 1,
  }));
}

/** Validate and normalise rows → {valid[], errors[]} */
function validateRows(rows) {
  const valid = [];
  const errors = [];
  for (const r of rows) {
    const kind = TYPE_MAP[(r.raw_type || '').toLowerCase().trim()];
    if (!kind) { errors.push({ row: r.row, msg: `Type inconnu : "${r.raw_type}"` }); continue; }
    if (!r.name) { errors.push({ row: r.row, msg: 'Nom manquant' }); continue; }
    valid.push({
      kind,
      name: r.name,
      custom_prompt: r.description || '',
      folder_path: r.folder_path || '',
      background: r.background || 'white',
      aspect_ratio: r.aspect_ratio || '1:1',
      category: r.category || '',
      priority: r.priority || 1,
      _row: r.row,
    });
  }
  return { valid, errors };
}

// ── Sub-components ───────────────────────────────────────────────────────────

function StatusBadge({ status }) {
  const map = {
    pending:   { icon: Clock,         cls: 'text-muted-foreground',   label: 'En attente' },
    running:   { icon: Loader2,       cls: 'text-primary animate-spin', label: 'En cours' },
    completed: { icon: CheckCircle2,  cls: 'text-green-600',           label: 'Terminé' },
    failed:    { icon: XCircle,       cls: 'text-destructive',         label: 'Erreur' },
    done:      { icon: CheckCircle2,  cls: 'text-green-600',           label: 'OK' },
    error:     { icon: XCircle,       cls: 'text-destructive',         label: 'Erreur' },
  };
  const { icon: Icon, cls, label } = map[status] || map.pending;
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] ${cls}`}>
      <Icon className="w-3 h-3" /> {label}
    </span>
  );
}

function PreviewTable({ rows, selected, onToggle, onToggleAll }) {
  const allSelected = selected.size === rows.length;
  const hasFolders = rows.some(r => r.folder_path);
  const hasBg = rows.some(r => r.background && r.background !== 'white');
  return (
    <div className="overflow-auto rounded-lg border border-border text-xs">
      <table className="w-full">
        <thead className="bg-secondary/30 sticky top-0">
          <tr>
            <th className="px-3 py-2 text-left w-8">
              <input type="checkbox" checked={allSelected} onChange={onToggleAll} />
            </th>
            <th className="px-3 py-2 text-left text-muted-foreground uppercase tracking-wider w-6">P</th>
            <th className="px-3 py-2 text-left text-muted-foreground uppercase tracking-wider">Type</th>
            <th className="px-3 py-2 text-left text-muted-foreground uppercase tracking-wider">Thème</th>
            {hasFolders && <th className="px-3 py-2 text-left text-muted-foreground uppercase tracking-wider">Dossier</th>}
            <th className="px-3 py-2 text-left text-muted-foreground uppercase tracking-wider">Nom</th>
            <th className="px-3 py-2 text-left text-muted-foreground uppercase tracking-wider">Prompt</th>
            {hasBg && <th className="px-3 py-2 text-left text-muted-foreground uppercase tracking-wider">Fond</th>}
          </tr>
        </thead>
        <tbody className="divide-y divide-border/50">
          {rows.map((r, i) => (
            <tr key={i} className={selected.has(i) ? 'bg-primary/5' : 'hover:bg-secondary/20'}>
              <td className="px-3 py-1.5">
                <input type="checkbox" checked={selected.has(i)} onChange={() => onToggle(i)} />
              </td>
              <td className="px-3 py-1.5 text-center text-muted-foreground">{r.priority}</td>
              <td className="px-3 py-1.5">
                <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${KIND_COLORS[r.kind] || 'bg-secondary'}`}>
                  {KIND_LABELS[r.kind] || r.kind}
                </span>
              </td>
              <td className="px-3 py-1.5 text-muted-foreground">{r.category || '—'}</td>
              {hasFolders && <td className="px-3 py-1.5 text-muted-foreground max-w-[120px] truncate" title={r.folder_path}>{r.folder_path || '—'}</td>}
              <td className="px-3 py-1.5 font-medium">{r.name}</td>
              <td className="px-3 py-1.5 text-muted-foreground max-w-xs truncate">{r.custom_prompt || '—'}</td>
              {hasBg && <td className="px-3 py-1.5 text-muted-foreground">{BG_LABELS[r.background] || r.background}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function JobProgress({ job, onDone, onCancel }) {
  const pct = job.total > 0 ? Math.round((job.progress / job.total) * 100) : 0;
  const done = job.status === 'completed';
  const cancelled = job.status === 'cancelled';
  const finished = done || cancelled;
  const errors = (job.items || []).filter(it => it.status === 'error').length;
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => { if (finished && onDone) onDone(); }, [finished]); // eslint-disable-line

  const handleCancel = async () => {
    if (!window.confirm("Annuler le batch ? Les images déjà générées sont conservées.")) return;
    setCancelling(true);
    try {
      await onCancel(job.id);
    } catch { /* job déjà mort après redeploy — l'endpoint le marque cancelled */ }
    finally { setCancelling(false); }
  };

  return (
    <div className="space-y-2 p-4 border border-border rounded-lg bg-card">
      <div className="flex items-center justify-between text-xs">
        <span className="font-medium">{cancelled ? 'Batch annulé' : 'Batch en cours'}</span>
        <StatusBadge status={job.status} />
      </div>
      <div className="w-full bg-secondary/40 rounded-full h-2">
        <div
          className={`h-2 rounded-full transition-all ${done ? 'bg-green-500' : cancelled ? 'bg-orange-400' : 'bg-primary'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="flex justify-between text-[11px] text-muted-foreground">
        <span>{job.progress}/{job.total} assets</span>
        <span>{pct}%</span>
      </div>
      {errors > 0 && (
        <p className="text-[11px] text-destructive">{errors} erreur(s) — vérifiez les logs.</p>
      )}
      {done && (
        <p className="text-[11px] text-green-600 font-medium">
          ✓ Génération terminée — rechargez la bibliothèque pour voir les nouveaux assets.
        </p>
      )}
      {cancelled && (
        <p className="text-[11px] text-orange-500 font-medium">
          Batch annulé — {job.progress} asset{job.progress > 1 ? 's' : ''} généré{job.progress > 1 ? 's' : ''}.
        </p>
      )}
      {!finished && (
        <button
          type="button"
          onClick={handleCancel}
          disabled={cancelling}
          className="text-[11px] text-destructive hover:underline disabled:opacity-50 mt-1"
        >
          {cancelling ? 'Annulation…' : '✕ Annuler le batch'}
        </button>
      )}
    </div>
  );
}

// ── Main component ───────────────────────────────────────────────────────────

export default function LibraryGenerator({ onAssetsGenerated }) {
  const [rows, setRows] = useState([]);
  const [errors, setErrors] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [activeJob, setActiveJob] = useState(null);
  const [quality, setQuality] = useState('fast');
  const [recentJobs, setRecentJobs] = useState([]);
  const [jobData, setJobData] = useState(null);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const fileInputRef = useRef(null);
  const pollRef = useRef(null);

  // Load recent jobs on mount
  useEffect(() => {
    loadRecentJobs();
    return () => clearInterval(pollRef.current);
  }, []); // eslint-disable-line

  const loadRecentJobs = async () => {
    setLoadingJobs(true);
    try { setRecentJobs(await getLibraryBatchJobs()); } catch { /* silent */ }
    finally { setLoadingJobs(false); }
  };

  // Poll the active job every 2s
  const startPolling = useCallback((jobId) => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const data = await getLibraryBatchJob(jobId);
        setJobData(data);
        if (data.status === 'completed' || data.status === 'failed') {
          clearInterval(pollRef.current);
          setActiveJob(null);
          loadRecentJobs();
          onAssetsGenerated?.();
        }
      } catch { clearInterval(pollRef.current); }
    }, 2000);
  }, []); // eslint-disable-line

  const handleFile = async (file) => {
    if (!file) return;
    const text = await file.text();
    try {
      const rawRows = file.name.endsWith('.json') ? parseJSON(text) : parseCSV(text);
      const { valid, errors: errs } = validateRows(rawRows);
      setRows(valid);
      setErrors(errs);
      setSelected(new Set(valid.map((_, i) => i)));
      if (errs.length) toast.warning(`${errs.length} ligne(s) ignorée(s) — voir les erreurs ci-dessous`);
      else toast.success(`${valid.length} assets chargés`);
    } catch (err) {
      toast.error(`Erreur de parsing : ${err.message}`);
    }
  };

  const launch = async (itemsToGenerate) => {
    if (!itemsToGenerate.length) { toast.error('Aucun asset sélectionné.'); return; }
    const tid = toast.loading(`Démarrage de la génération (${itemsToGenerate.length} assets)…`);
    try {
      const res = await startLibraryBatch(itemsToGenerate.map(r => ({
        kind: r.kind,
        name: r.name,
        custom_prompt: r.custom_prompt,
        folder_path: r.folder_path || '',
        background: r.background || 'white',
        aspect_ratio: r.aspect_ratio || '1:1',
        category: r.category || '',
        priority: r.priority || 1,
      })), quality);
      toast.dismiss(tid);
      toast.success(`Job démarré — ${res.total} assets en cours de génération`);
      setActiveJob(res.job_id);
      const init = { id: res.job_id, status: 'pending', progress: 0, total: res.total, items: [] };
      setJobData(init);
      startPolling(res.job_id);
    } catch (err) {
      toast.dismiss(tid);
      toast.error(err?.response?.data?.detail || 'Erreur de démarrage');
    }
  };

  const selectedRows = rows.filter((_, i) => selected.has(i));
  const testRows = selectedRows.slice(0, 3);

  // Summary stats
  const stats = useMemo(() => {
    const counts = { characters: 0, decors: 0, objects: 0 };
    selectedRows.forEach(r => { if (counts[r.kind] !== undefined) counts[r.kind]++; });
    const folders = new Set(selectedRows.map(r => r.folder_path).filter(Boolean));
    return { ...counts, folders: folders.size };
  }, [selectedRows]);

  return (
    <div className="space-y-5">

      {/* ── Upload ── */}
      <div
        className="border-2 border-dashed border-border rounded-xl p-8 text-center cursor-pointer hover:border-primary/40 hover:bg-primary/5 transition-all"
        onClick={() => fileInputRef.current?.click()}
        onDragOver={e => e.preventDefault()}
        onDrop={e => { e.preventDefault(); handleFile(e.dataTransfer.files[0]); }}
      >
        <Upload className="w-8 h-8 text-muted-foreground/40 mx-auto mb-2" />
        <p className="text-sm font-medium">Glissez un fichier CSV ou JSON</p>
        <p className="text-xs text-muted-foreground mt-1">
          Colonnes : <code className="bg-secondary/50 px-1 rounded">category</code>,{' '}
          <code className="bg-secondary/50 px-1 rounded">name</code>,{' '}
          <code className="bg-secondary/50 px-1 rounded">description</code>
        </p>
        <Button variant="outline" size="sm" className="mt-3 h-7 text-xs" onClick={e => { e.stopPropagation(); fileInputRef.current?.click(); }}>
          Parcourir
        </Button>
        <input ref={fileInputRef} type="file" accept=".csv,.json" className="hidden" onChange={e => handleFile(e.target.files?.[0])} />
      </div>

      {/* ── Validation errors ── */}
      {errors.length > 0 && (
        <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 space-y-1">
          <p className="text-xs font-semibold text-destructive">Lignes ignorées :</p>
          {errors.map((e, i) => <p key={i} className="text-[11px] text-destructive/80">Ligne {e.row} : {e.msg}</p>)}
        </div>
      )}

      {/* ── Preview table ── */}
      {rows.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <p className="text-xs text-muted-foreground">
              <span className="font-semibold text-foreground">{rows.length}</span> assets chargés ·{' '}
              <span className="font-semibold text-primary">{selected.size}</span> sélectionnés
            </p>
            {selected.size > 0 && (
              <div className="flex gap-2 text-[11px] text-muted-foreground flex-wrap">
                {stats.characters > 0 && <span className="px-1.5 py-0.5 rounded-full bg-blue-500/10 text-blue-600">{stats.characters} personnages</span>}
                {stats.decors > 0 && <span className="px-1.5 py-0.5 rounded-full bg-green-500/10 text-green-600">{stats.decors} décors</span>}
                {stats.objects > 0 && <span className="px-1.5 py-0.5 rounded-full bg-orange-500/10 text-orange-600">{stats.objects} objets</span>}
                {stats.folders > 0 && <span className="px-1.5 py-0.5 rounded-full bg-secondary text-muted-foreground">{stats.folders} dossiers</span>}
              </div>
            )}
          </div>
          <div className="max-h-72 overflow-y-auto">
            <PreviewTable
              rows={rows}
              selected={selected}
              onToggle={i => setSelected(prev => { const n = new Set(prev); n.has(i) ? n.delete(i) : n.add(i); return n; })}
              onToggleAll={() => setSelected(prev => prev.size === rows.length ? new Set() : new Set(rows.map((_, i) => i)))}
            />
          </div>

          {/* ── Qualité ── */}
          <div className="flex items-center gap-3 p-3 rounded-lg border border-border bg-secondary/20 text-xs">
            <span className="text-muted-foreground font-medium shrink-0">Modèle :</span>
            <div className="flex gap-1.5">
              {[
                { value: 'fast', label: 'Fast', sub: '~$0.02 / image', desc: 'Nano Banana standard' },
                { value: 'high', label: 'Pro', sub: '~$0.09 / image', desc: 'Nano Banana Pro — meilleure qualité' },
              ].map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setQuality(opt.value)}
                  className={`flex flex-col px-3 py-2 rounded-md border transition-all text-left ${
                    quality === opt.value
                      ? 'border-primary bg-primary/5 text-foreground'
                      : 'border-border text-muted-foreground hover:border-primary/40'
                  }`}
                >
                  <span className="font-semibold">{opt.label}</span>
                  <span className={`text-[10px] mt-0.5 ${quality === opt.value ? 'text-primary' : 'text-muted-foreground/70'}`}>{opt.sub}</span>
                </button>
              ))}
            </div>
            <span className="text-muted-foreground/60 text-[11px]">
              {quality === 'fast' ? 'Nano Banana standard — rapide' : 'Nano Banana Pro — meilleure compréhension du prompt'}
            </span>
          </div>

          {/* ── Actions ── */}
          <div className="flex gap-2 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              className="h-8 gap-1.5 text-xs"
              onClick={() => launch(testRows)}
              disabled={!!activeJob || testRows.length === 0}
              title={`Générer les ${testRows.length} premières lignes sélectionnées`}
            >
              <Play className="w-3.5 h-3.5" />
              Test ({testRows.length} assets)
            </Button>
            <Button
              size="sm"
              className="h-8 gap-1.5 text-xs"
              onClick={() => launch(selectedRows)}
              disabled={!!activeJob || selectedRows.length === 0}
            >
              {activeJob ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />}
              Générer batch ({selectedRows.length} assets)
            </Button>
          </div>
        </div>
      )}

      {/* ── Active job progress ── */}
      {jobData && (
        <JobProgress
          job={jobData}
          onDone={() => setJobData(d => d ? { ...d } : d)}
          onCancel={async (jobId) => {
            await cancelLibraryBatch(jobId);
            setJobData(d => d ? { ...d, status: 'cancelled' } : d);
            setActiveJob(null);
            loadRecentJobs();
          }}
        />
      )}

      {/* ── Recent jobs ── */}
      {(recentJobs.length > 0 || loadingJobs) && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Jobs récents</p>
            <button onClick={loadRecentJobs} className="text-muted-foreground hover:text-foreground p-0.5">
              <RefreshCw className={`w-3 h-3 ${loadingJobs ? 'animate-spin' : ''}`} />
            </button>
          </div>
          <div className="space-y-1.5">
            {recentJobs.map(j => (
              <div key={j.id} className="flex items-center justify-between text-xs bg-secondary/20 rounded-md px-3 py-1.5 gap-2">
                <span className="text-muted-foreground shrink-0">{new Date(j.created_at).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                <span className="shrink-0">{j.progress}/{j.total} assets</span>
                <StatusBadge status={j.status} />
                {j.status === 'running' && (
                  <button
                    type="button"
                    className="text-[10px] text-destructive hover:underline shrink-0"
                    onClick={async () => {
                      try {
                        await cancelLibraryBatch(j.id);
                        loadRecentJobs();
                        toast.success('Job annulé');
                      } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
                    }}
                  >
                    Annuler
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
